import { create } from "zustand";
import { GET } from "@/services/api";

const API_URL = import.meta.env.VITE_API_URL;

interface User {
  username: string;
  role: string;
  appStatus: string;
  appOnHold: string;
  institutionId: string | null;
}

interface AuthState {
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  clearError: () => void;
  refreshToken: () => Promise<void>;
  getSessionInformation: () => Promise<void>;
  login: (email: string, pwd: string, nav: any) => Promise<void>;
  logout: (nav?: any) => void;
  setAccessToken: (token: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: "",
  isLoading: true,
  error: null,
  user: null,
  setAccessToken: (token) => {
    set({ accessToken: token });
  },
  clearError: () => set({ error: null }),
  getSessionInformation: async () => {
    try {
      set({ isLoading: true });
      const res = await GET(`/api/auth/me`);
      set({ user: res });
    } catch (e) {
      set({ accessToken: null });
    } finally {
      set({ isLoading: false });
    }
  },
  refreshToken: async () => {
    try {
      set({ isLoading: true });
      const res = await fetch(`/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        set({ accessToken: data.accessToken });
      }
    } catch (e) {
      set({ accessToken: null });
    } finally {
      set({ isLoading: false });
    }
  },
  login: async (username, password, nav) => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Login failed");
      }

      const data = await res.json();
      set({ accessToken: data.accessToken });
      const info = await GET(`/api/auth/me`);

      set({ isLoading: false, user: info });
      if (nav && info.role === "APPLICANT") {
        nav("/application");
      } else {
        nav("/")
      }
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  logout: async (nav) => {
    const response = await fetch(`${API_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    if (response.ok) {

      set({ accessToken: null, user: null });
      if (nav) {
        nav("/login");
      }
    }
  },
}));
